import _ from "lodash";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";
import {
  CURRICULUM_TYPE_MYP,
  ELEMENT_TYPE_PYP_LEARNER_PROFILE,
  ELEMENT_TYPE_PYP_KEY_CONCEPT,
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_MYP_RELATED_CONCEPT,
  ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT,
  ELEMENT_TYPE_MYP_LEARNER_PROFILE,
  ELEMENT_TYPE_MYP_KEY_CONCEPT,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_ATL,
  ELEMENT_TYPE_DP_SYLLABUS,
  ELEMENT_TYPE_DP_LEARNER_PROFILE,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_ATL,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  PLANNER_ELEMENT_NESTED_CHECKLIST,
  TEMPLATE_FIELD_INQUIRY_QUESTIONS,
  TEMPLATE_FIELD_INQUIRY_QUESTIONS_V2,
  TEMPLATE_FIELD_LINES_OF_INQUIRY,
  TEMPLATE_FIELD_UN_GOALS,
  TEMPLATE_FIELD_UN_GOALS_ASSESSMENT,
  CURRICULUM_TYPE_PYP,
} from "Constants/stringConstants";
import { getOptionsFromField } from "Worksheet/modules/WorksheetUtils";
import { FIELD_UID_ADD_LEARNING_GOALS } from "Worksheet/modules/WorksheetConstants";
import {
  getInquiryQuestionsNodes,
  getInquiryQuestionsV2Nodes,
  getLinesOfInquiryNodes,
  getNodesWithGradeLevels,
  getResolvedMinimalTreeSet,
  getPlannerElementNodesFromFieldOptions,
  getFieldValue,
} from "Assessments/modules/Utils";
import { getAddLearningGoalsDropdownOptions } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getPYPElementSetFromNodes } from "Utils";
import { getIsNewGradingSetupEnabled } from "ClassRoom/modules/utils";

export const ASSESSMENT_LEARNING_GOALS_OPTIONS = {
  LEARNING_GOALS: "LEARNING_GOALS",
  MYP_OBJECTIVES: "MYP_OBJECTIVES",
  STANDARDS: "STANDARDS",
};

// Field keys for MYP objectives
export const MYP_OBJECTIVES_FIELD_KEYS = [
  "objectivesMYP",
  "interdisciplinaryCriteriaMYP",
];

export const getAssessmentLearningGoalsDropdownOptions = ({
  t,
  learningGoalsOptions,
  curriculumType,
  disableStandardsOption = false,
}) => {
  const isMYPCurriculum = _.isEqual(curriculumType, CURRICULUM_TYPE_MYP);
  // Check if MYP objectives exist in learning goals options
  const hasMypObjectives = _.some(learningGoalsOptions, ({ key }) =>
    _.includes(MYP_OBJECTIVES_FIELD_KEYS, key)
  );

  // Filter out MYP objectives from learning goals options for the main Learning Goals tab
  const filteredLearningGoalsOptions = _.filter(
    learningGoalsOptions,
    ({ key }) => !_.includes(MYP_OBJECTIVES_FIELD_KEYS, key)
  );

  const isNewGradingSetupEnabled = getIsNewGradingSetupEnabled();
  let standardsLabel = "";

  if (isNewGradingSetupEnabled) {
    const fieldKey = STANDARD_UIDS[curriculumType];
    standardsLabel = _.find(filteredLearningGoalsOptions, { key: fieldKey })
      ?.label;
  }

  const options = [
    {
      label: t("common:learning_goals"),
      key: ASSESSMENT_LEARNING_GOALS_OPTIONS.LEARNING_GOALS,
      disabled: _.isEmpty(filteredLearningGoalsOptions),
      disabledTooltip: t("worksheet:no_learning_goals_available"),
      tooltipPlacement: "left",
    },
    {
      label: standardsLabel,
      key: ASSESSMENT_LEARNING_GOALS_OPTIONS.STANDARDS,
      ...(disableStandardsOption
        ? {
            disabled: true,
            disabledTooltip: t("classRoom:add_subject_to_tag_with_label", {
              label: standardsLabel,
            }),
            tooltipPlacement: "left",
          }
        : {}),
    },
  ];

  // Add MYP Objectives option only for MYP curriculum
  if (isMYPCurriculum) {
    options.splice(1, 0, {
      label: t("common:myp_objectives"),
      key: ASSESSMENT_LEARNING_GOALS_OPTIONS.MYP_OBJECTIVES,
      tooltipPlacement: "left",
    });
  }

  return options;
};

// * Learning goals query variables
export const getAssessmentLearningGoalsQueryVariable = ({
  t,
  courseId,
  academicYearId,
  courseSubjects,
  assessmentFields,
  assignmentSubjects,
  field_list: fieldList,
  defaultOptions: _defaultOptions,
  unitPlanTemplate,
  plannerElementSets,
}) => {
  const defaultOptions =
    getOptionsFromField({
      t,
      fieldList: fieldList,
      fieldUid: FIELD_UID_ADD_LEARNING_GOALS,
    }) || _defaultOptions;

  const unitPlan = _.find(assessmentFields, { uid: "unitPlan" });

  const classSubjects = !_.isEmpty(assignmentSubjects)
    ? assignmentSubjects
    : courseSubjects;

  // * Get learning goal options after mapping type from fieldList and filtering benchmarks
  const learningGoalOptionsUnMapped = getAddLearningGoalsDropdownOptions({
    fieldList: fieldList,
    options: defaultOptions,
    subjects: classSubjects,
    resourceFields: assessmentFields,
    unitPlanTemplate,
    plannerElementSets,
  });

  const standardUidValues = _.values(STANDARD_UIDS);

  const allLearningGoals = _.filter(
    _.map(learningGoalOptionsUnMapped, option => {
      const key = _.get(option, "key");
      const config = _.get(fieldList, [key, "config", "plannerElementConfig"]);
      return {
        ...option,
        type: _.get(config, "type"),
        subType: _.get(config, "subType"),
        isPlannerElement: _.includes(
          ["PLANNER_ELEMENT", "PYP_PLANNER_ELEMENT"],
          _.get(fieldList, [key, "config", "type"])
        ),
      };
    }),
    ({ key }) => !_.includes(standardUidValues, key)
  );

  const learningGoalsOptions = _.filter(allLearningGoals, ({ type }) => type);

  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters: { academicYearId },
  });

  const gradeDependent = ["atlsMYP", "atlsUBD", "atlDP"];
  const subjectDependent = [
    "objectivesMYP",
    "relatedConceptsMYP",
    "syllabusContentDP",
  ];
  const subTypeDependent = _.map(
    _.filter(learningGoalsOptions, ({ subType }) => subType),
    "key"
  );

  const allDependentKeys = _.concat(
    gradeDependent,
    subjectDependent,
    subTypeDependent
  );

  const getElementTypes = keys =>
    _.map(
      _.filter(learningGoalsOptions, ({ key }) => _.includes(keys, key)),
      "type"
    );

  const gradeDependentLearningGoalVariables = {
    elementTypes: getElementTypes(gradeDependent),
    filters: {
      grades: _.map(_.get(courseDetails, "grades"), "id"),
    },
  };

  const subjectDependentLearningGoalVariables = {
    elementTypes: getElementTypes(subjectDependent),
    filters: {
      subjects: _.map(_.get(courseDetails, "subjects"), "id"),
    },
  };

  const subTypeDependentLearningGoalVariables = {
    elementTypes: getElementTypes(subTypeDependent),
    filters: {
      subTypes: ["INTERDISCIPLINARY_CRITERIA"],
    },
  };

  const curriculumDependentLearningGoalVariables = {
    elementTypes: _.map(
      _.filter(
        learningGoalsOptions,
        ({ key }) => !_.includes(allDependentKeys, key)
      ),
      "type"
    ),
  };

  // Separate MYP objectives from all learning goals
  const mypObjectivesOptions = _.filter(allLearningGoals, ({ key }) =>
    _.includes(MYP_OBJECTIVES_FIELD_KEYS, key)
  );

  // Filter out MYP objectives from learning goals (will be shown in separate tab)
  const filteredLearningGoals = _.filter(
    allLearningGoals,
    ({ key }) => !_.includes(MYP_OBJECTIVES_FIELD_KEYS, key)
  );

  return {
    unitPlan: unitPlan,
    allLearningGoals: filteredLearningGoals,
    mypObjectivesOptions,
    learningGoalsQueryVariables: {
      gradeDependentLearningGoalVariables,
      subjectDependentLearningGoalVariables,
      subTypeDependentLearningGoalVariables,
      curriculumDependentLearningGoalVariables,
    },
  };
};
/**
 * Recursively builds nested child nodes from a flat list of nodes
 * @param {Object} params - Function parameters
 * @returns {Array} Array of nested child nodes with their descendants
 */
const getNestedChildNodes = ({ nodes, levels, index, parent, hasLevels }) => {
  if (hasLevels && index >= _.size(levels)) return [];

  const filteredNodes = _.isUndefined(parent)
    ? _.filter(
        nodes,
        hasLevels ? { levelId: levels[index] } : { depth: index + 1 }
      )
    : _.filter(
        nodes,
        hasLevels
          ? { levelId: levels[index], parent }
          : { depth: index + 1, parent }
      );

  return _.map(filteredNodes, item => {
    const id = _.get(item, "id");
    return {
      id,
      label: _.get(item, "label"),
      depth: index + 1,
      childNodes: getNestedChildNodes({
        nodes,
        levels,
        hasLevels,
        parent: id,
        index: index + 1,
      }),
    };
  });
};

/**
 *
 * @param {*} data
 * @returns Learning Goals Data in Tree Formate
 */
export const getAssessmentLearningGoalsTreeData = ({
  learningGoalsData,
  allLearningGoals,
}) => {
  const rootNodes = _.map(allLearningGoals, learningGoal => {
    const key = _.get(learningGoal, "key");
    const type = _.get(learningGoal, "type");
    const subType = _.get(learningGoal, "subType");
    const item = _.find(learningGoalsData, {
      ...(type && { type }),
      ...(subType && { subType }),
      ...(!type && key && { key }),
    });
    if (!item) return null;

    let levels = _.orderBy(_.uniq(_.map(item?.nodes, "levelId")));
    const hasLevels = _.compact(levels).length > 0;
    if (!hasLevels) levels = [];
    let depths = _.orderBy(_.uniq(_.map(item?.nodes, "depth")));
    if (hasLevels) depths = [];

    return {
      levels: levels,
      depths: depths,
      hasLevels: hasLevels,
      id: _.get(learningGoal, "key"),
      type: _.get(learningGoal, "type"),
      label: _.get(learningGoal, "label"),
      subType: _.get(learningGoal, "subType"),
      childNodes: getNestedChildNodes({
        levels,
        hasLevels,
        nodes: _.get(item, "nodes", []),
        index: hasLevels ? 0 : _.min(depths) - 1,
      }),
    };
  });

  return _.compact(rootNodes);
};

/**
 *
 * @param { id, treeData }
 * @returns Get all leaf nodes
 */
export const getAllLeafNodes = (treeData, id) => {
  if (_.isEmpty(treeData) || _.isNil(id)) {
    return [];
  }

  // * Optimized iterative approach to find the target node and collect leaf nodes
  let targetNode = null;
  const searchQueue = [...treeData];

  // * Find target node using index-based iteration (faster than shift())
  for (let queueIndex = 0; queueIndex < searchQueue.length; queueIndex++) {
    const current = searchQueue[queueIndex];

    if (_.get(current, "id") === id) {
      targetNode = current;
      break;
    }

    const childNodes = _.get(current, "childNodes");
    if (childNodes && childNodes.length > 0) {
      searchQueue.push(...childNodes);
    }
  }

  if (!targetNode) {
    return [];
  }

  // * Collect all leaf nodes using index-based iteration
  const leafNodes = [];
  const leafQueue = [targetNode];

  for (
    let leafQueueIndex = 0;
    leafQueueIndex < leafQueue.length;
    leafQueueIndex++
  ) {
    const node = leafQueue[leafQueueIndex];
    const childNodes = _.get(node, "childNodes");

    if (!childNodes || childNodes.length === 0) {
      leafNodes.push(node.id);
    } else {
      leafQueue.push(...childNodes);
    }
  }

  return leafNodes;
};

/**
 *
 * @param { options allLearningGoals suffix }
 * @returns
 */
export const getLearningGoalsWithFormattedOptionsForSideNav = ({
  suffix,
  options,
  allLearningGoals,
}) => {
  if (allLearningGoals) {
    // * Learning goal options
    return [
      { key: "root", children: _.map(allLearningGoals, "key") },
      ..._.map(allLearningGoals, item => {
        const option = _.find(
          options,
          item?.subType
            ? { type: item.type, subType: item.subType }
            : { type: item.type }
        );
        return {
          tier: 1,
          parent: "root",
          suffix: suffix,
          key: _.get(item, "key"),
          title: _.get(item, "label"),
          childIds: _.map(_.get(option, "childNodes", []), "id"),
        };
      }),
    ];
  }

  // * Secondary Sidebar options
  return [
    { key: "root", children: _.map(options, "id") },
    ..._.map(options, item => ({
      tier: 1,
      parent: "root",
      suffix: suffix,
      key: _.get(item, "id"),
      title: _.get(item, "label"),
      childIds: _.map(_.get(item, "childNodes", []), "id"),
    })),
  ];
};

/**
 *
 * @param { allLearningGoals, assessmentFields }
 * @returns All selected learning goals initial value
 */
export const getAssessmentLearningGoalsInitialValue = ({
  allLearningGoals,
  assessmentFields,
}) => {
  const values = {};
  _.forEach(allLearningGoals, ({ key }) => {
    const field = _.find(assessmentFields, { uid: key });
    _.set(values, key, _.get(field, "value", []) || []);
  });
  return values;
};

/**
 *
 * @param { data, query, isMatch = false }
 * @returns Searched Tree Data
 */
export const getSearchedLearningGoals = ({ data, query, isMatch = false }) => {
  const childNodes = _.get(data, "childNodes", []);
  const isCurrentMatch = isMatch || _.includes(_.toLower(data?.label), query);
  const item = _.pick(data, ["id", "label", "depth"]);

  // Base case: leaf node
  if (_.isEmpty(childNodes)) return isCurrentMatch ? [item] : [];

  // Process child nodes
  const matchedChildren = [];
  _.forEach(childNodes, childNode => {
    const childResults = getSearchedLearningGoals({
      query,
      data: childNode,
      isMatch: isCurrentMatch,
    });
    if (!_.isEmpty(childResults)) matchedChildren.push(...childResults);
  });

  // If current node matches or has matched children, include it with matched children
  if (isCurrentMatch || !_.isEmpty(matchedChildren)) {
    return [{ ...item, childNodes: matchedChildren }];
  }

  return [];
};

/**
 * Get nodes and unitSpecificNodes for a specific learning goal based on planner element type or field key for non-planner elements
 * @param {Object} params - Function parameters
 * @returns {Object} Nodes data - returns object with nodes and unitSpecificNodes for the elements
 */
export const getLearningGoalData = ({
  fieldKey,
  plannerElementType,
  unitPlanFields,
  unitPlanTemplate,
  subFields,
  curriculumType,
  assessmentFields,
  isPlannerElement,
  plannerElementNodes,
}) => {
  const resourceNodes = getResolvedMinimalTreeSet({
    uid: fieldKey,
    fields: assessmentFields,
    curriculumType,
  });

  const unitPlanNodes = getResolvedMinimalTreeSet({
    uid: fieldKey,
    fields: unitPlanFields,
    curriculumType,
  });

  const allNodes = isPlannerElement
    ? _.uniqBy(
        [
          ...(plannerElementNodes || []),
          ...(unitPlanNodes || []),
          ...(resourceNodes || []),
        ],
        "id"
      )
    : [];

  switch (plannerElementType) {
    // Subject dependent planner element types
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_LEARNING_STANDARD:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_SYLLABUS:
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT:
    case ELEMENT_TYPE_MYP_OBJECTIVES: {
      // For non-pyp curriculum, grades are there in associatedParents of nodes
      // So extract them and add to the node explicitly to show in grade levels column
      const filteredUnitPlanNodes = getNodesWithGradeLevels({
        nodes: unitPlanNodes,
      });
      const filteredAllNodes = getNodesWithGradeLevels({
        nodes: allNodes,
      });

      const { nodes: unitSpecificNodes = [] } =
        getPYPElementSetFromNodes({ nodes: filteredUnitPlanNodes }) || {};
      const { nodes = [] } =
        getPYPElementSetFromNodes({ nodes: filteredAllNodes }) || {};

      return {
        nodes,
        unitSpecificNodes,
      };
    }

    // Subject dependent PYP standards
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD: {
      const { nodes: unitSpecificNodes = [] } =
        getPYPElementSetFromNodes({
          nodes: _.filter(unitPlanNodes, ({ depth }) => depth > 0),
          startDepth: 1,
        }) || {};
      const { nodes = [] } =
        getPYPElementSetFromNodes({
          nodes: _.filter(allNodes, ({ depth }) => depth > 0),
          startDepth: 1,
        }) || {};

      return {
        nodes,
        unitSpecificNodes,
      };
    }

    // Grade dependent planner element types
    case ELEMENT_TYPE_PYP_ATL:
    case ELEMENT_TYPE_MYP_ATL:
    case ELEMENT_TYPE_DP_ATL:
    case ELEMENT_TYPE_UBD_ATL:
    case PLANNER_ELEMENT_NESTED_CHECKLIST: {
      const { nodes: unitSpecificNodes = [] } =
        getPYPElementSetFromNodes({ nodes: unitPlanNodes }) || {};
      const { nodes = [] } =
        getPYPElementSetFromNodes({ nodes: allNodes }) || {};

      return { nodes, unitSpecificNodes };
    }

    // Independent planner element types (neither dependent on grades nor subjects)
    case ELEMENT_TYPE_PYP_LEARNER_PROFILE:
    case ELEMENT_TYPE_MYP_LEARNER_PROFILE:
    case ELEMENT_TYPE_DP_LEARNER_PROFILE:
    case ELEMENT_TYPE_PYP_KEY_CONCEPT:
    case ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT:
    case ELEMENT_TYPE_MYP_KEY_CONCEPT: {
      return {
        unitSpecificNodes: unitPlanNodes,
        nodes: allNodes,
      };
    }

    //Non- planner element types
    default:
      switch (fieldKey) {
        case TEMPLATE_FIELD_LINES_OF_INQUIRY: {
          const nodes =
            getLinesOfInquiryNodes({
              unitPlanFields,
              fieldKey,
            })?.nodes || [];

          return { nodes };
        }

        case TEMPLATE_FIELD_INQUIRY_QUESTIONS: {
          const nodes =
            getInquiryQuestionsNodes({
              unitPlanFields,
              fieldKey,
              subFields,
            })?.nodes || [];

          return { nodes };
        }

        case TEMPLATE_FIELD_INQUIRY_QUESTIONS_V2: {
          const fieldList = _.get(unitPlanTemplate, "field_list", {});
          const fieldObj = _.get(fieldList, fieldKey, {});
          const dataField = _.get(fieldObj, "dataField", {});
          const nodes =
            getInquiryQuestionsV2Nodes({
              unitPlanFields,
              unitPlanFieldList: fieldList,
              fieldKey,
              dataField,
            })?.nodes || [];

          return { nodes };
        }

        case TEMPLATE_FIELD_UN_GOALS:
        case TEMPLATE_FIELD_UN_GOALS_ASSESSMENT: {
          const fieldList = _.get(unitPlanTemplate, "field_list", {});
          const fieldObj = _.get(fieldList, fieldKey, {});
          const fieldObjConfigOptions = _.get(fieldObj, "config.options", []);
          const resourceFieldValue = getFieldValue({
            fields: assessmentFields,
            uid: fieldKey,
          });
          const nodes =
            getPlannerElementNodesFromFieldOptions({
              fieldObjConfigOptions,
              resourceFieldValue,
              mode: "edit",
            }) || [];
          return { nodes };
        }
        default:
          return [];
      }
  }
};

/**
 * Helper function to generate variables for getPlannerElementNodeSetIdsOfCurriculumQuery
 * @param {Object} params - Parameters object
 * @param {string} params.curriculumId - The curriculum ID
 * @param {string} params.curriculumNodeInterfaceType - The curriculum node interface type
 * @param {Array} params.plannerElementTypes - Array of planner element types
 * @returns {Object} Variables for the query
 */
export const getPlannerElementNodeSetIdsOfCurriculumVariables = ({
  curriculumId,
  curriculumNodeInterfaceType,
  plannerElementTypes,
}) => {
  const plannerElementNodeSetFilters = {
    types: [...plannerElementTypes],
  };

  return {
    id: curriculumId,
    type: curriculumNodeInterfaceType,
    plannerElementNodeSetFilters,
  };
};

/**
 * @param {Object} params - Function parameters
 * @param {Array} params.plannerElementNodeSets - Array of planner element node sets from Step 1
 * @param {Object} params.queryVariables - Specific query variables (e.g., gradeDependentLearningGoalVariables)
 * @param {string} params.academicYearId - Academic year ID from mapStateToProps
 * @returns {Object|null} Final query variables with plannerElementNodeSetIds and plannerElementNodeFilters
 */
export const getPlannerElementNodeSetsFromIdsVariables = ({
  plannerElementNodeSets,
  queryVariables = {},
  academicYearId,
}) => {
  if (_.isEmpty(plannerElementNodeSets)) {
    return null;
  }

  const elementTypes = _.get(queryVariables, "elementTypes", []);
  // Filter plannerElementNodeSets to only include those whose type matches elementTypes
  const filteredNodeSets = _.filter(plannerElementNodeSets, nodeSet =>
    _.includes(elementTypes, _.get(nodeSet, "type"))
  );

  const plannerElementNodeSetIds = _.map(filteredNodeSets, "id");
  const filters = _.get(queryVariables, "filters");

  return {
    plannerElementNodeSetIds,
    ...(filters ? { plannerElementNodeFilters: filters } : {}),
    ...(academicYearId ? { academicYearId } : {}),
  };
};

/**
 * Transform planner element node sets from new API response format to learning goals data format
 * @param {Object} params - Parameters object
 * @param {Array} params.plannerElementNodeSets - Array of planner element node sets from multiNode response
 * @returns {Array} Transformed learning goals data in format { id, type, label, nodes }
 */
export const getPlannerElementNodeSetData = ({
  plannerElementNodeSets,
  isSubTypeDependent = false,
}) => {
  const transformedData = _.map(plannerElementNodeSets, nodeSet => {
    const id = _.get(nodeSet, "plannerElementSet.id");
    const type = _.get(nodeSet, "plannerElementSet.type");
    const label = _.get(nodeSet, "plannerElementSet.label");
    const wrapper = _.get(nodeSet, "plannerElementNodeSetWrapperV2", {});
    const edges = _.get(wrapper, "publishedData.edges", []);
    const nodes = _.map(edges, edge => _.get(edge, "node"));
    return {
      id,
      type,
      label,
      nodes,
      isSubTypeDependent,
    };
  });
  return _.filter(transformedData, item => !_.isNil(item.type));
};

export const getLearningGoalsHeaderDropdownOptions = ({ t, unitPlan }) => {
  const unitLabel = _.get(
    unitPlan,
    "resolvedMinimalTree.unitPlan.title.value",
    ""
  );
  const unitKey = _.get(unitPlan, "value", "");

  return [
    {
      label: t("scopeAndSequence:tagged_in_label", { label: unitLabel }),
      key: unitKey,
    },
    { label: t("common:all"), key: "all" },
  ];
};

export const parseInitialSelectedStandards = ({
  curriculumType,
  standardUID,
  assessmentFields,
}) => {
  const standardField = _.find(assessmentFields, { uid: standardUID });

  const standards =
    curriculumType === CURRICULUM_TYPE_PYP
      ? _.get(standardField, "resolvedMinimalTree.benchmarks", [])
      : _.get(standardField, "resolvedMinimalTree.standards", []);

  return _.mapValues(
    _.groupBy(standards, item =>
      _.get(item, "standardsWrapper.standardSet.id")
    ),
    items => _.map(items, "id")
  );
};
